import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import Select, { MultiValue, StylesConfig, components, ValueContainerProps } from "react-select";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminOrderService,
    AdminOrder,
    GetAdminOrdersParams
} from "../../services/admin.order.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClipboardList,
    faSearch,
    faCircleNotch,
    faTriangleExclamation,
    faBox,
    faUser,
    faTruck,
    faEye,
    faTimes,
    faUndo,
    faDollarSign,
    faFileExcel,
    faRotate} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminOrdersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
const CustomValueContainer = (props: ValueContainerProps<any, true>) => {
    const { children, innerProps } = props;
    const scrollRef = React.useRef<HTMLDivElement | null>(null);

    // Merge refs to ensure react-select's internal ref also works
    const ref = (instance: HTMLDivElement | null) => {
        scrollRef.current = instance;
        if (innerProps) {
            if (typeof innerProps.ref === 'function') {
                innerProps.ref(instance);
            } else if (innerProps.ref && 'current' in innerProps.ref) {
                (innerProps.ref as any).current = instance;
            }
        }
    };

    React.useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            const onWheel = (e: WheelEvent) => {
                if (e.deltaY !== 0 && el.scrollWidth > el.clientWidth) {
                    e.preventDefault();
                    el.scrollLeft += e.deltaY;
                }
            };
            el.addEventListener("wheel", onWheel, { passive: false });
            return () => el.removeEventListener("wheel", onWheel);
        }
    }, []);

    return (
        <components.ValueContainer {...props} innerProps={{ ...(innerProps || {}), ref }}>
            {children}
        </components.ValueContainer>
    );
};

const AdminOrdersPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "ORDERS", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [orders, setOrders] = useState<AdminOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchParams] = useSearchParams();
    const [statusFilter, setStatusFilter] = useState<number[]>([]);
    const [searchCustomer, setSearchCustomer] = useState(searchParams.get("customerEmail") || "");
    const [searchOrder, setSearchOrder] = useState("");
    const [showMargins, setShowMargins] = useState(false);

    // Helper to format Date to YYYY-MM-DD
    const formatDateToYYYYMMDD = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper to format Date/Time to readable locale string
    const formatDateTimeLocale = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-VE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Default dates (last 7 days)
    const getDefaultDates = () => {
        const now = new Date();
        const start = new Date();
        start.setDate(now.getDate() - 7);

        return {
            start: formatDateToYYYYMMDD(start),
            end: formatDateToYYYYMMDD(now)
        };
    };

    const getInitialDates = () => {
        const urlStart = searchParams.get("startDate");
        const urlEnd = searchParams.get("endDate");
        if (urlStart && urlEnd) {
            return { start: urlStart, end: urlEnd };
        }
        const defaultDates = getDefaultDates();
        if (searchParams.get("customerEmail")) {
            const now = new Date();
            return {
                start: `${now.getFullYear()}-01-01`,
                end: formatDateToYYYYMMDD(now)
            };
        }
        return defaultDates;
    };

    const initialDates = getInitialDates();
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [exportingExcel, setExportingExcel] = useState(false);

    const handleExportExcel = async () => {
        try {
            setExportingExcel(true);
            const blob = await adminOrderService.downloadOrdersExcel({
                SearchStringCustomer: searchCustomer || undefined,
                SearchStringOrder: searchOrder || undefined,
                start: startDate || undefined,
                end: endDate || undefined,
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const cleanStart = (startDate || "inicio").replace(/-/g, "");
            const cleanEnd = (endDate || "fin").replace(/-/g, "");
            link.download = `Reporte_Ordenes_${cleanStart}_${cleanEnd}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error("Error downloading orders excel:", err);
            Swal.fire({
                icon: "error",
                title: "Error al exportar",
                text: "No se pudo generar el archivo Excel de las órdenes.",
            });
        } finally {
            setExportingExcel(false);
        }
    };

    const fetchOrders = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetAdminOrdersParams = {
                page: page,
                pageSize: size,
                SearchStringCustomer: searchCustomer || undefined,
                SearchStringOrder: searchOrder || undefined,
                start: startDate || undefined,
                end: endDate || undefined,
            };

            const response = await adminOrderService.getOrders(params);

            if (response.statusCode === 200) {
                setOrders(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching admin orders:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchOrders(1, perPage);
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchOrders(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchOrders(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchOrders(1, perPage);
    };

    const handleCancelOrder = async (orderId: number) => {
        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: "Esta acción cancelará el envío seleccionado.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, cancelar",
            cancelButtonText: "No, volver",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminOrderService.cancelOrder({ Id: orderId });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Cancelada",
                        text: "La orden ha sido cancelada con éxito.",
                    });
                    fetchOrders(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error cancelling order:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleRefundOrder = async (orderId: number) => {
        const result = await Swal.fire({
            title: "¿Reembolsar pago?",
            text: "Esta acción reembolsará el pago en Stripe para la orden seleccionada.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#10b981",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, reembolsar",
            cancelButtonText: "No, cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminOrderService.refundOrder({ Id: orderId });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Reembolsado",
                        text: "El reembolso ha sido procesado exitosamente.",
                    });
                    fetchOrders(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error refunding order:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const columns: TableColumn<AdminOrder>[] = [
        {
            name: "Nro. Tracking UPS",
            selector: (row) => row.tracking,
            sortable: true,
            cell: (row) => (
                <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
                    {row.tracking}
                </div>
            ),
            width: "260px",
        },
        {
            name: "Creación",
            selector: (row) => row.creationDate,
            sortable: true,
            cell: (row) => (
                <div className={styles.dateInfo}>
                    {formatDateTimeLocale(row.creationDate)}
                </div>
            ),
            width: "220px",
        },
        {
            name: "Tipo Servicio",
            selector: (row) => row.serviceType ? row.serviceType.join(" ") : "",
            sortable: false,
            cell: (row) => {
                if (row.externalServices && row.externalServices.length > 0) {
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {row.externalServices.map((srv, index) => (
                                <div key={index} style={{ display: "flex", flexDirection: "column" }}>
                                    <span style={{ fontSize: "13px", color: "#475569", fontWeight: "500" }}>
                                        {srv.serviceName}
                                    </span>
                                    {srv.codeRef?.toUpperCase() === "PRN" && srv.externalReference && (
                                        <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
                                            PRN: {srv.externalReference}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }

                if (row.serviceType && row.serviceType.length > 0) {
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {row.serviceType.map((st, index) => (
                                <span key={index} style={{ fontSize: "13px", color: "#475569", fontWeight: "500" }}>
                                    {st}
                                </span>
                            ))}
                        </div>
                    );
                }

                return <div style={{ fontSize: "13px", color: "#94a3b8" }}>-</div>;
            },
            width: "180px",
        },
        {
            name: "Cliente",
            selector: (row) => row.customer.firstName,
            sortable: true,
            cell: (row) => (
                <div className={styles.customerInfo}>
                    <span className={styles.customerName}>{row.customer.firstName} {row.customer.lastName}</span>
                    <span className={styles.customerEmail}>{row.customer.email}</span>
                </div>
            ),
            width: "200px",
        },
        {
            name: "Courier",
            selector: (row) => row.carrier,
            sortable: true,
            cell: (row) => (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "6px 0" }}>
                    <span className={styles.carrierBadge} style={{ width: "fit-content" }}>
                        {row.carrier || "-"}
                    </span>
                    <span style={{ fontSize: "12px", color: "#475569", fontWeight: "500" }}>
                        {row.carrierService || "-"}
                    </span>
                    <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}>
                        {row.trackingCarrier || "-"}
                    </span>
                </div>
            ),
            width: "220px",
        },
        ...(showMargins ? [
            {
                name: "Pagado",
                selector: (row: AdminOrder) => (row.totalPaid ?? row.margin?.totalPaid ?? ((row.margin?.upsCharged ?? 0) + (row.margin?.courierCharged ?? 0) + (row.margin?.logistica ?? 0))),
                sortable: true,
                cell: (row: AdminOrder) => {
                    const total = row.totalPaid ?? row.margin?.totalPaid ?? ((row.margin?.upsCharged ?? 0) + (row.margin?.courierCharged ?? 0) + (row.margin?.logistica ?? 0));
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "14px", fontWeight: 800, color: "#1e293b" }}>
                                ${total.toFixed(2)}
                            </span>
                            <span style={{ fontSize: "11px", color: "#64748b" }}>
                                Total cliente
                            </span>
                        </div>
                    );
                },
                width: "120px",
            },
            {
                name: "UPS (Tarifa)",
                selector: (row: AdminOrder) => row.margin?.upsCharged ?? 0,
                sortable: true,
                cell: (row: AdminOrder) => {
                    const upsCharged = row.margin?.upsCharged ?? 0;
                    const upsGross = row.margin?.upsGross !== undefined
                        ? row.margin.upsGross
                        : (upsCharged - (row.margin?.upsCost ?? 0));
                    const pickup = row.margin?.upsPickupCharged ?? 0;
                    const arrastre = row.margin?.upsArrastreCharged ?? (upsCharged - pickup);
                    const hasPickup = pickup > 0;

                    return upsCharged > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                                ${upsCharged.toFixed(2)}
                            </span>
                            {hasPickup && (
                                <span style={{ fontSize: "11px", color: "#64748b" }}>
                                    Arrastre: ${arrastre.toFixed(2)} | Recogida: ${pickup.toFixed(2)}
                                </span>
                            )}
                            <span style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#1e293b"
                            }}>
                                UPS Gross: <span style={{ color: upsGross >= 0 ? "#059669" : "#dc2626" }}>${upsGross.toFixed(2)}</span>
                            </span>
                        </div>
                    ) : (
                        <span style={{ fontSize: "13px", color: "#94a3b8" }}>-</span>
                    );
                },
                width: "170px",
            },
            {
                name: "Courier (Tarifa)",
                selector: (row: AdminOrder) => row.margin?.courierTotal ?? ((row.margin?.courierCharged ?? 0) + (row.margin?.logistica ?? 0)),
                sortable: true,
                cell: (row: AdminOrder) => {
                    const courierTotal = row.margin?.courierTotal ?? ((row.margin?.courierCharged ?? 0) + (row.margin?.logistica ?? 0));
                    return courierTotal > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                                {row.carrier || "-"}
                            </span>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                                ${courierTotal.toFixed(2)}
                            </span>
                            <span style={{ fontSize: "11px", color: "#475569" }}>
                                Comisión 20%: <span style={{ fontWeight: 600, color: "#059669" }}>${(row.margin?.courierCommission ?? 0).toFixed(2)}</span>
                            </span>
                        </div>
                    ) : (
                        <span style={{ fontSize: "13px", color: "#94a3b8" }}>-</span>
                    );
                },
                width: "160px",
            },
            {
                name: "Gross",
                selector: (row: AdminOrder) => row.margin?.gross ?? 0,
                sortable: true,
                cell: (row: AdminOrder) => {
                    const gross = row.margin?.gross ?? 0;
                    const totalPaid = row.margin?.totalPaid ?? row.totalPaid ?? 0;
                    const grossPercent = row.margin?.grossPercent !== undefined
                        ? row.margin.grossPercent
                        : (totalPaid > 0 ? Math.round((gross / totalPaid) * 1000) / 10 : 0);

                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                                <span style={{
                                    fontSize: "14px",
                                    fontWeight: 800,
                                    color: gross >= 0 ? "#059669" : "#dc2626"
                                }}>
                                    ${gross.toFixed(2)}
                                </span>
                                {totalPaid > 0 && (
                                    <span style={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        padding: "1px 5px",
                                        borderRadius: "4px",
                                        backgroundColor: gross >= 0 ? "#d1fae5" : "#fee2e2",
                                        color: gross >= 0 ? "#065f46" : "#991b1b"
                                    }}>
                                        {grossPercent > 0 ? `+${grossPercent}%` : `${grossPercent}%`}
                                    </span>
                                )}
                            </div>
                            {(row.margin?.logistica ?? 0) > 0 && (
                                <span style={{ fontSize: "11px", color: "#475569" }}>
                                    Logística: <span style={{ fontWeight: 600, color: "#059669" }}>${(row.margin?.logistica ?? 0).toFixed(2)}</span>
                                </span>
                            )}
                        </div>
                    );
                },
                width: "150px",
            },
        ] as TableColumn<AdminOrder>[] : []),
        {
            name: "Estatus",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => row.status ? (
                <span className={`${styles.statusBadge} ${styles.statusGeneric}`} style={{ fontSize: "10px" }}>
                    {row.status}
                </span>
            ) : null,
            width: "180px",
        },
        {
            name: "Estado de Pago",
            selector: (row: AdminOrder) => {
                const isVoided = Boolean(row.voided || row.status?.toUpperCase() === "CANCELLED");
                const isRefunded = Boolean(row.refunded);
                const isPaid = Boolean(row.paid);

                if (isVoided && isRefunded) return "Reembolsada";
                if (isVoided && !isRefunded) return "Reembolso Pendiente";
                if (isPaid) return "Pagada";
                return "Pago Pendiente";
            },
            sortable: true,
            cell: (row: AdminOrder) => {
                const isVoided = Boolean(row.voided || row.status?.toUpperCase() === "CANCELLED");
                const isRefunded = Boolean(row.refunded);
                const isPaid = Boolean(row.paid);

                let label = "Pago Pendiente";
                let badgeStyle: React.CSSProperties = {
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "inline-block",
                };

                if (isVoided && isRefunded) {
                    label = "Reembolsada";
                    badgeStyle = { ...badgeStyle, backgroundColor: "#e0e7ff", color: "#3730a3" };
                } else if (isVoided && !isRefunded) {
                    label = "Reembolso Pendiente";
                    badgeStyle = { ...badgeStyle, backgroundColor: "#ffedd5", color: "#c2410c" };
                } else if (isPaid) {
                    label = "Pagada";
                    badgeStyle = { ...badgeStyle, backgroundColor: "#d1fae5", color: "#065f46" };
                } else {
                    label = "Pago Pendiente";
                    badgeStyle = { ...badgeStyle, backgroundColor: "#fef3c7", color: "#92400e" };
                }

                return <span style={badgeStyle}>{label}</span>;
            },
            width: "180px",
        },
        {
            name: "Acciones",
            width: "160px",
            cell: (row) => {
                const canCancel = permissionCheckerService.checkPermission(adminData, "ORDERS", "CANCEL");
                const canRefund = permissionCheckerService.checkPermission(adminData, "ORDERS", "REFUND");

                const isAlreadyAffected = Boolean(row.voided || row.finished || row.status?.toUpperCase() === "CANCELLED");
                const isCancelled = Boolean(row.voided || row.status?.toUpperCase() === "CANCELLED");
                const isPaid = Boolean(row.paid);
                const isRefunded = Boolean(row.refunded);

                const canApplyRefund = isCancelled && isPaid;
                const isRefundDisabled = !canApplyRefund || isRefunded || !canRefund;

                return (
                    <div className="d-flex gap-2" style={{ paddingRight: "10px" }}>
                        <button
                            className={styles.detailButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/admin/order/${row.id}`, "_blank");
                            }}
                            title="Ver detalle"
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </button>
                        {canCancel && (
                            <button
                                className={styles.cancelButton}
                                title={isAlreadyAffected ? "Orden ya cancelada / finalizada" : "Cancelar orden"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelOrder(row.id);
                                }}
                                disabled={isAlreadyAffected}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                        {canRefund && (
                            <button
                                className={styles.refundButton}
                                title={
                                    isRefunded
                                        ? "Orden ya reembolsada"
                                        : !canApplyRefund
                                        ? "Solo aplica para órdenes canceladas y pagadas"
                                        : "Reembolsar orden"
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRefundOrder(row.id);
                                }}
                                disabled={isRefundDisabled}
                            >
                                <FontAwesomeIcon icon={faUndo} />
                            </button>
                        )}
                    </div>
                );
            },
            right: true,
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "70px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    const customSelectStyles: StylesConfig<{ value: number, label: string }, true> = {
        control: (base, state) => ({
            ...base,
            minHeight: '42px',
            maxHeight: '42px',
            borderRadius: '8px',
            borderColor: state.isFocused ? 'var(--elPaket-primary-color)' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 3px var(--elPaket-primary-color-light, rgba(232, 77, 48, 0.1))' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? 'var(--elPaket-primary-color)' : '#cbd5e1',
            },
            fontSize: '14px',
            background: 'white',
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            maxHeight: '40px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: 'var(--elPaket-primary-color-light, rgba(232, 77, 48, 0.1))',
            borderRadius: '6px',
            color: 'var(--elPaket-primary-color)',
            fontWeight: '600',
            minWidth: 'fit-content',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: 'var(--brand-black)',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'var(--brand-black)',
            '&:hover': {
                backgroundColor: 'rgba(232, 77, 48, 0.2)',
                color: 'var(--elPaket-primary-color-hover, #D4402A)',
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            backgroundColor: state.isSelected
                ? 'var(--elPaket-primary-color)'
                : state.isFocused
                    ? '#f1f5f9'
                    : 'transparent',
            color: state.isSelected ? 'white' : '#475569',
            '&:active': {
                backgroundColor: 'var(--elPaket-primary-color)',
                color: 'white',
            },
        }),
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminOrdersContainer}>
                <DashboardCard
                    icon={faClipboardList}
                    title="Gestión de Órdenes"
                    description="Visualiza y filtra todas las órdenes realizadas en el sistema."
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Desde</label>
                                <input
                                    type="date"
                                    className={styles.filterInput}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>Hasta</label>
                                <input
                                    type="date"
                                    className={styles.filterInput}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por cliente (Nombre, Email, DNI...)"
                                    className={styles.searchInput}
                                    value={searchCustomer}
                                    onChange={(e) => setSearchCustomer(e.target.value)}
                                />
                            </div>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faBox} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por Id, Tracking, Courier o Servicio..."
                                    className={styles.searchInput}
                                    value={searchOrder}
                                    onChange={(e) => setSearchOrder(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary} style={{ width: "auto" }}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>

                        <div className={styles.toolbarRow}>
                            <button
                                type="button"
                                className={styles.btnRefresh}
                                onClick={() => fetchOrders(currentPage, perPage)}
                                disabled={loading}
                                title="Refrescar listado de órdenes"
                            >
                                <FontAwesomeIcon icon={loading ? faCircleNotch : faRotate} spin={loading} /> Refrescar
                            </button>
                            <button
                                type="button"
                                className={`${styles.btnMargins} ${showMargins ? styles.btnMarginsBlack : styles.btnMarginsYellow}`}
                                onClick={() => setShowMargins((prev) => !prev)}
                                title="Mostrar u ocultar el margen bruto por guía"
                            >
                                <FontAwesomeIcon icon={faDollarSign} /> {showMargins ? "Ocultar márgenes" : "Ver márgenes"}
                            </button>
                            <button
                                type="button"
                                className={styles.btnExcelGreen}
                                onClick={handleExportExcel}
                                disabled={exportingExcel || loading}
                                title="Exportar todas las órdenes filtradas a Excel"
                            >
                                <FontAwesomeIcon icon={exportingExcel ? faCircleNotch : faFileExcel} spin={exportingExcel} /> {exportingExcel ? "Exportando..." : "Exportar a Excel"}
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchOrders(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : orders.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faClipboardList} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron órdenes con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={orders}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando lista de órdenes...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>
        </AdminDashboardLayout>
    );
};

export default AdminOrdersPage;
